/**
 * Where everybody stands, during the game rather than after it.
 *
 * Three gaps, one answer. The table draws every seat but YOUR OWN - you are
 * not shown a chair because you know where you are sitting - which quietly
 * meant your score and your tricks were the only ones you could not see. And a
 * running score nobody can rank is a list of numbers rather than a standing.
 *
 * So this is ordered by score and includes you, marked. Whatever the table
 * shows around the ring, this is the column you read to know how the game is
 * going.
 *
 * Entirely declared: a game with no `seatScore` and no `seatTally` renders
 * nothing at all, which is right for Crazy Eights - there is no running score
 * to stand on.
 *
 * ## Folded on a phone
 *
 * 110px of a 584px column, and most of it is on the screen twice already: the
 * rows repeat the ring, and your own row is in the top bar. What only this can
 * say is the ranking, so that is what the folded line says - where you stand,
 * and who is ahead - and a tap opens the rest.
 *
 * It does not remember being open. A legend is a rule you learn once;
 * standings is a thing you glance at and close.
 */

import { useState } from 'react';
import type { MatchView } from '../net/match.js';
import type { GameLayoutWire } from '../net/session.js';
import { UNANSWERED, tallyText } from '../render/tally.js';
import { sidesOf } from '../render/teams.js';
import { ordinal, summarise } from '../render/standings.js';
import type { StandingRow } from '../render/standings.js';
import { useNarrow } from './use-narrow.js';

/** One per-seat number from a view field the layout named. */
function numberAt(view: MatchView, field: string | undefined, seat: number): number | null {
  if (!field) return null;
  const raw = view.view[field];
  if (!Array.isArray(raw)) return null;
  const value = (raw as unknown[])[seat];
  return typeof value === 'number' ? value : null;
}

/**
 * The rows, whether the game stands on chairs or on sides.
 *
 * One shape for both, which is what lets everything below - the ordering, the
 * fold, the summary - be written once. A partnership game scores a pair
 * together, so four independent rows would be four numbers that decide
 * nothing; each side still lists its players, because you want to see who your
 * partner is and how they are doing.
 */
function rowsOf(
  view: MatchView,
  layout: GameLayoutWire,
  sides: ReturnType<typeof sidesOf>,
): StandingRow[] | null {
  const score = layout.seatScore;
  const tally = layout.seatTally;

  if (sides) {
    const nameOf = (seat: number) =>
      view.seats.find((info) => info.seat === seat)?.displayName ?? `Seat ${String(seat + 1)}`;

    return sides.map((side, index) => ({
      key: `side-${String(index)}`,
      name: side.seats.map(nameOf).join(' & '),
      score: side.score,
      tally: tally
        ? side.seats
            .map(
              (seat) =>
                tallyText(numberAt(view, tally.value, seat), numberAt(view, tally.target, seat)) ??
                UNANSWERED,
            )
            .join('  ')
        : null,
      you: view.seat !== null && side.seats.indexOf(view.seat) >= 0,
    }));
  }

  if (!score && !tally) return null;

  return view.seats.map((seat) => ({
    key: `seat-${String(seat.seat)}`,
    name: seat.displayName,
    score: score ? numberAt(view, score.field, seat.seat) : null,
    // What a negative target means, and why zero is not the test, is stated
    // once in `render/tally.ts`. The ring shows the same pair, and the two used
    // to carry separate copies of the rule.
    tally: tally
      ? (tallyText(numberAt(view, tally.value, seat.seat), numberAt(view, tally.target, seat.seat)) ??
        UNANSWERED)
      : null,
    you: seat.seat === view.seat,
  }));
}

export function Standings({ view, layout }: { view: MatchView; layout: GameLayoutWire }) {
  const narrow = useNarrow();
  const [open, setOpen] = useState(false);

  const sides = sidesOf(view, layout);
  const rows = rowsOf(view, layout, sides);
  if (!rows || rows.length === 0) return null;

  const lowIsBetter = (sides ? layout.teams?.lowIsBetter : layout.seatScore?.lowIsBetter) === true;

  /*
   * Standings, not seat order - seat order is what the ring already shows.
   *
   * Ties and unscored rows compare equal and keep the order they came in,
   * which is seat order: `sort` is stable by specification, so this needs no
   * tie-break of its own.
   */
  const ordered = rows.slice().sort((a, b) => {
    if (a.score === null || b.score === null) return 0;
    return lowIsBetter ? a.score - b.score : b.score - a.score;
  });

  const tally = layout.seatTally;
  const summary = summarise(ordered, lowIsBetter);

  /*
   * Folded, and only where the room is short.
   *
   * Nothing changes above 719px, where the column has the height for the whole
   * table - and the summary is drawn from the same rows, so the two cannot
   * disagree about who is winning.
   */
  if (narrow && !open && summary) {
    const leader = summary.leader.you ? summary.behind : summary.leader;
    return (
      <button
        type="button"
        className="standings standings-folded"
        onClick={() => setOpen(true)}
        aria-expanded={false}
        aria-label="Standings, tap to open"
      >
        <span className="standings-place">
          {summary.leader.you && summary.position === 1 && !summary.shared
            ? 'You lead'
            : `${ordinal(summary.position)} of ${String(summary.of)}`}
        </span>
        {leader ? (
          <span className="standings-leader">
            {/* Who to catch, or who is catching you. Named either way, because
                "2nd of 4" without a name is half a fact. */}
            <span className="standings-name">{leader.name}</span>
            <span className="standings-score">{leader.score ?? '—'}</span>
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <div className="standings">
      <div className="standings-head">
        <span className="field-label">Standings</span>
        {tally?.label ? <span className="field-help">{tally.label} won / bid</span> : null}

        {/* Only where it was folded to begin with. On a desktop this is the
            whole thing and there is nothing to close it back to. */}
        {narrow ? (
          <button type="button" className="text-button" onClick={() => setOpen(false)}>
            Close
          </button>
        ) : null}
      </div>

      <ol className="standings-rows">
        {ordered.map((row) => (
          <li key={row.key} className={row.you ? 'mine' : undefined}>
            <span className="standings-name">
              {row.name}
              {/* Marked rather than moved: you belong in the order, and pinning
                  you to the top would hide whether you are winning. In a
                  partnership game it marks the side you are ON, which is the
                  thing you are actually following. */}
              {row.you ? <span className="standings-you">you</span> : null}
            </span>

            {row.tally !== null ? <span className="standings-tally">{row.tally}</span> : null}

            {layout.seatScore || sides ? (
              <span className="standings-score">{row.score ?? '—'}</span>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}
